import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { PlanBanner } from './components/PlanBanner.tsx';
import { AnnouncementBar } from './components/AnnouncementBar.tsx';
import { SupportBanner } from './components/SupportBanner.tsx';
import { Apps } from './screens/Apps.tsx';
import { Dashboard } from './screens/Dashboard.tsx';
import { Design } from './screens/Design.tsx';
import { Support } from './screens/manage/Support.tsx';
import { BrandMark } from './components/BrandMark.tsx';
import { Button, EmptyState } from './components/ui.tsx';
import { t } from './i18n/index.ts';
import { installBackHandler, inApp, syncBars } from './lib/native.ts';
import { PlacesProvider } from './lib/places.tsx';
import { useSession } from './lib/session.tsx';
import { useUi } from './lib/ui-state.tsx';
import { Floor } from './screens/Floor.tsx';
import { Kitchen } from './screens/Kitchen.tsx';
import { CustomerDisplay } from './screens/CustomerDisplay.tsx';
import { Login, SsoReturn } from './screens/Login.tsx';
import { SingleSignOn } from './screens/manage/SingleSignOn.tsx';
import { Authorize } from './screens/Authorize.tsx';
import { Security, SecurityBanner, SecuritySetup } from './screens/Security.tsx';
import { Order } from './screens/Order.tsx';
import { SECTIONS } from './screens/manage/common.tsx';
import { Business } from './screens/manage/Business.tsx';
import { Currencies } from './screens/manage/Currencies.tsx';
import { TaxRates } from './screens/manage/TaxRates.tsx';
import { Locations } from './screens/manage/Locations.tsx';
import { ApprovalRules } from './screens/manage/ApprovalRules.tsx';
import { Automations } from './screens/manage/Automations.tsx';
import { DocTemplateEditor, DocTemplates } from './screens/manage/DocTemplates.tsx';
import { Fields } from './screens/manage/Fields.tsx';
import { Loyalty } from './screens/manage/Loyalty.tsx';
import { Online as OnlineSetup } from './screens/manage/Online.tsx';
import { Online } from './screens/Online.tsx';
import { Team } from './screens/Team.tsx';
import { TeamSetup } from './screens/manage/Team.tsx';
import { Phones } from './screens/manage/Phones.tsx';
import { Phone } from './screens/Phone.tsx';
import { Fiscal } from './screens/manage/Fiscal.tsx';
import { DataImport } from './screens/manage/DataImport.tsx';
import { FiscalJournal } from './screens/manage/FiscalJournal.tsx';
import { Menu } from './screens/manage/Menu.tsx';
import { Options } from './screens/manage/Options.tsx';
import { Printers } from './screens/manage/Printers.tsx';
import { PriceTimes } from './screens/manage/PriceTimes.tsx';
import { Purchasing } from './screens/manage/Purchasing.tsx';
import { Recipes } from './screens/manage/Recipes.tsx';
import { Setup } from './screens/manage/Setup.tsx';
import { Staff } from './screens/manage/Staff.tsx';
import { Roles } from './screens/manage/Roles.tsx';
import { Stock } from './screens/manage/Stock.tsx';
import { Warehouse } from './screens/manage/Warehouse.tsx';
import { Tables } from './screens/manage/Tables.tsx';
import { Receipts } from './screens/Receipts.tsx';
import { Guests } from './screens/Guests.tsx';
import { Accounting } from './screens/Accounting.tsx';
import { Bank } from './screens/Bank.tsx';
import { Developer } from './screens/Developer.tsx';
import { Mailbox } from './screens/Mailbox.tsx';
import { Crm } from './screens/Crm.tsx';
import { Distribution } from './screens/Distribution.tsx';
import { ProjectPage, Projects } from './screens/Projects.tsx';
import { Payroll } from './screens/Payroll.tsx';
import { Budget } from './screens/Budget.tsx';
import { Insights } from './screens/Insights.tsx';
import { AiActivity } from './screens/AiActivity.tsx';
import { Protocol } from './screens/Protocol.tsx';
import { Assets } from './screens/Assets.tsx';
import { Procurement } from './screens/Procurement.tsx';
import { Hotel } from './screens/Hotel.tsx';
import { Manufacturing } from './screens/Manufacturing.tsx';
import { Rental } from './screens/Rental.tsx';
import { Fleet } from './screens/Fleet.tsx';
import { Frontdesk, FrontdeskKiosk } from './screens/Frontdesk.tsx';
import { Cleaning } from './screens/Cleaning.tsx';
import { Privacy } from './screens/Privacy.tsx';
import { PlanBilling } from './screens/PlanBilling.tsx';
import { Signup } from './screens/Signup.tsx';
import { Learning, LearningCourse, LearningEditor, LearningManage } from './screens/Learning.tsx';
import { Consolidation, ConsolidationMapping, ConsolidationPeriod } from './screens/Consolidation.tsx';
import { Esg } from './screens/Esg.tsx';
import { Iot, IotDevice } from './screens/Iot.tsx';
import { PaymentDetail, Payments } from './screens/Payments.tsx';
import { Shipping } from './screens/Shipping.tsx';
import { Lunch } from './screens/Lunch.tsx';
import { Todo } from './screens/Todo.tsx';
import { Maintenance } from './screens/Maintenance.tsx';
import { Quality } from './screens/Quality.tsx';
import { Repairs } from './screens/Repairs.tsx';
import { Plm } from './screens/Plm.tsx';
import { Barcode } from './screens/Barcode.tsx';
import { Website } from './screens/Website.tsx';
import { Events } from './screens/Events.tsx';
import { Appointments } from './screens/Appointments.tsx';
import { Surveys } from './screens/Surveys.tsx';
import { LiveChat } from './screens/LiveChat.tsx';
import { Discuss } from './screens/Discuss.tsx';
import { Calendar } from './screens/Calendar.tsx';
import { Knowledge } from './screens/Knowledge.tsx';
import { Subscriptions } from './screens/Subscriptions.tsx';
import { Marketing } from './screens/Marketing.tsx';
import { Outreach } from './screens/Outreach.tsx';
import { Sms } from './screens/Sms.tsx';
import { Social } from './screens/Social.tsx';
import { Calls } from './screens/Calls.tsx';
import { Helpdesk } from './screens/Helpdesk.tsx';
import { FieldService } from './screens/Field.tsx';
import { TaskProject, TasksHome } from './screens/Tasks.tsx';
import { People } from './screens/People.tsx';
import { Documents } from './screens/Documents.tsx';
import { Shop } from './screens/Shop.tsx';
import { Approvals } from './screens/Approvals.tsx';
import { DashboardPage, Dashboards } from './screens/Dashboards.tsx';
import { MyReport, MyReports, ReportBuilder } from './screens/MyReports.tsx';
import { Invoices } from './screens/Invoices.tsx';
import { Till } from './screens/Till.tsx';
import { Reports } from './screens/Reports.tsx';
import { Shift } from './screens/Shift.tsx';
import { OfflineTill } from './screens/OfflineTill.tsx';

/** Each role lands on the screen it uses most (GOLDEN-RULE.md, rule 9). */
function Home() {
  const { can } = useSession();
  // People who look after the business open on the dashboard; floor staff keep their own screen.
  if (can('reports.view') || can('accounting.view') || can('crm.use')) return <Navigate to="/home" replace />;
  if (can('restaurant.order')) return <Navigate to="/pos" replace />;
  if (can('restaurant.kitchen')) return <Navigate to="/kitchen" replace />;
  if ((can('hotel.book') || can('hotel.housekeeping')) && !can('restaurant.order')) return <Navigate to="/hotel" replace />;
  if (can('mrp.produce') && !can('restaurant.order') && !can('restaurant.kitchen')) return <Navigate to="/manufacturing" replace />;
  if (can('pos.shift')) return <Navigate to="/shift" replace />;
  if (can('invoicing.manage') && !can('accounting.view')) return <Navigate to="/invoices" replace />;
  if (can('accounting.view')) return <Navigate to="/accounting" replace />;
  return <ManageHome />;
}

/** The back office opens on the first section this person may use. */
function ManageHome() {
  const { can } = useSession();
  const first = SECTIONS.find((s) => can(s.permission));
  return first ? <Navigate to={first.to} replace /> : <NoAccess />;
}

function NoAccess() {
  const { signOut } = useSession();
  return (
    <main className="center-page">
      <EmptyState
        icon="alert"
        title={t('error.no_access')}
        body={t('error.no_access_body')}
        action={
          <Button icon="logout" onClick={() => void signOut()}>
            {t('nav.sign_out')}
          </Button>
        }
      />
    </main>
  );
}

/**
 * In the phone app: a notification opens its page without loading the app again, the back
 * button closes dialogs first, and the phone's bars take the colours of the page.
 */
function PhoneApp() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { theme } = useUi();
  useEffect(() => {
    if (!inApp()) return;
    installBackHandler();
    window.__erpOpen = (path: string) => {
      navigate(path);
      return true;
    };
    return () => {
      window.__erpOpen = undefined;
    };
  }, [navigate]);
  useEffect(() => {
    if (!inApp()) return;
    const id = requestAnimationFrame(syncBars);
    const media = matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', syncBars);
    return () => {
      cancelAnimationFrame(id);
      media.removeEventListener('change', syncBars);
    };
  }, [pathname, theme]);
  return null;
}

function Guard({ permission, children }: { permission: string | string[]; children: ReactNode }) {
  const { can } = useSession();
  return (Array.isArray(permission) ? permission.some(can) : can(permission)) ? children : <NoAccess />;
}

export function App() {
  const { me, loading } = useSession();
  if (loading) {
    return (
      <main className="center-page" aria-busy="true">
        <span className="brand-logo cube">
          <BrandMark size={30} />
        </span>
      </main>
    );
  }
  // A new business signs itself up at /signup; anyone else not signed in gets the sign-in page.
  if (!me) {
    const path = window.location.pathname;
    // The business's provider sends people back to /sso after a single sign-on.
    return /^\/signup\/?$/.test(path) ? <Signup /> : /^\/sso\/?$/.test(path) ? <SsoReturn /> : <Login />;
  }
  // The days to set up password and authenticator app have passed: only that is open.
  if (me.security?.blocked) return <SecuritySetup />;
  // An app sent the person here to allow it (Developer → Apps): that page alone, without the menus.
  if (/^\/oauth\/authorize\/?$/.test(window.location.pathname)) return <Authorize />;
  return (
    <PlacesProvider key={me.userId}>
      <BrowserRouter>
        <StaffBanners />
        <PhoneApp />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/security" element={<Security />} />
          <Route path="/sso" element={<Navigate to="/" replace />} />
          <Route
            path="/manage/sign-in"
            element={
              <Guard permission="company.manage">
                <SingleSignOn />
              </Guard>
            }
          />
          <Route
            path="/pos"
            element={
              <Guard permission="restaurant.order">
                <Floor />
              </Guard>
            }
          />
          <Route
            path="/till/:id?"
            element={
              <Guard permission="restaurant.order">
                <Till />
              </Guard>
            }
          />
          <Route
            path="/pos/order/:id"
            element={
              <Guard permission="restaurant.order">
                <Order />
              </Guard>
            }
          />
          <Route
            path="/online"
            element={
              <Guard permission="online.view">
                <Online />
              </Guard>
            }
          />
          <Route
            path="/display/:register"
            element={
              <Guard permission="pos.view">
                <CustomerDisplay />
              </Guard>
            }
          />
          <Route
            path="/kitchen"
            element={
              <Guard permission="restaurant.kitchen">
                <Kitchen />
              </Guard>
            }
          />
          <Route
            path="/shift"
            element={
              <Guard permission="pos.view">
                <Shift />
              </Guard>
            }
          />
          <Route
            path="/offline"
            element={
              <Guard permission="pos.pay">
                <OfflineTill />
              </Guard>
            }
          />
          <Route
            path="/receipts"
            element={
              <Guard permission="sales.view">
                <Receipts />
              </Guard>
            }
          />
          <Route
            path="/guests"
            element={
              <Guard permission="customers.view">
                <Guests />
              </Guard>
            }
          />
          <Route
            path="/reports"
            element={
              <Guard permission="reports.view">
                <Reports />
              </Guard>
            }
          />
          <Route
            path="/invoices"
            element={
              <Guard permission="invoicing.view">
                <Invoices />
              </Guard>
            }
          />
          <Route
            path="/accounting"
            element={
              <Guard permission="accounting.view">
                <Accounting />
              </Guard>
            }
          />
          <Route
            path="/mailbox"
            element={
              <Guard permission="mailbox.view">
                <Mailbox />
              </Guard>
            }
          />
          <Route
            path="/developer"
            element={
              <Guard permission="developer.view">
                <Developer />
              </Guard>
            }
          />
          <Route
            path="/bank"
            element={
              <Guard permission="bank.view">
                <Bank />
              </Guard>
            }
          />
          <Route
            path="/crm"
            element={
              <Guard permission="crm.use">
                <Crm />
              </Guard>
            }
          />
          <Route
            path="/projects"
            element={
              <Guard permission="construction.view">
                <Projects />
              </Guard>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <Guard permission="construction.view">
                <ProjectPage />
              </Guard>
            }
          />
          <Route
            path="/routes"
            element={
              <Guard permission={['distribution.field', 'distribution.cash']}>
                <Distribution />
              </Guard>
            }
          />
          <Route
            path="/payroll"
            element={
              <Guard permission="payroll.view">
                <Payroll />
              </Guard>
            }
          />
          <Route
            path="/insights"
            element={
              <Guard permission="ai.use">
                <Insights />
              </Guard>
            }
          />
          <Route
            path="/ai-activity"
            element={
              <Guard permission="ai.audit">
                <AiActivity />
              </Guard>
            }
          />
          <Route
            path="/budget"
            element={
              <Guard permission="budget.view">
                <Budget />
              </Guard>
            }
          />
          <Route
            path="/protocol"
            element={
              <Guard permission="protocol.view">
                <Protocol />
              </Guard>
            }
          />
          <Route
            path="/assets"
            element={
              <Guard permission="assets.view">
                <Assets />
              </Guard>
            }
          />
          <Route
            path="/procurement"
            element={
              <Guard permission="procurement.view">
                <Procurement />
              </Guard>
            }
          />
          <Route
            path="/hotel"
            element={
              <Guard permission="hotel.view">
                <Hotel />
              </Guard>
            }
          />
          <Route
            path="/manufacturing"
            element={
              <Guard permission="mrp.view">
                <Manufacturing />
              </Guard>
            }
          />
          <Route
            path="/shop"
            element={
              <Guard permission="shop.view">
                <Shop />
              </Guard>
            }
          />
          <Route
            path="/rental"
            element={
              <Guard permission="rental.view">
                <Rental />
              </Guard>
            }
          />
          <Route
            path="/maintenance"
            element={
              <Guard permission="maint.report">
                <Maintenance />
              </Guard>
            }
          />
          <Route
            path="/repairs"
            element={
              <Guard permission="repair.view">
                <Repairs />
              </Guard>
            }
          />
          <Route
            path="/events"
            element={
              <Guard permission="events.view">
                <Events />
              </Guard>
            }
          />
          <Route
            path="/calendar"
            element={
              <Guard permission="calendar.use">
                <Calendar />
              </Guard>
            }
          />
          <Route
            path="/knowledge/:id?"
            element={
              <Guard permission="knowledge.use">
                <Knowledge />
              </Guard>
            }
          />
          <Route
            path="/discuss"
            element={
              <Guard permission="discuss.use">
                <Discuss />
              </Guard>
            }
          />
          <Route
            path="/livechat"
            element={
              <Guard permission="livechat.answer">
                <LiveChat />
              </Guard>
            }
          />
          <Route
            path="/surveys"
            element={
              <Guard permission="surveys.view">
                <Surveys />
              </Guard>
            }
          />
          <Route
            path="/appointments"
            element={
              <Guard permission="appointments.view">
                <Appointments />
              </Guard>
            }
          />
          <Route
            path="/website"
            element={
              <Guard permission="website.view">
                <Website />
              </Guard>
            }
          />
          <Route
            path="/barcode"
            element={
              <Guard permission="barcode.use">
                <Barcode />
              </Guard>
            }
          />
          <Route
            path="/plm"
            element={
              <Guard permission="plm.view">
                <Plm />
              </Guard>
            }
          />
          <Route
            path="/quality"
            element={
              <Guard permission="quality.alert">
                <Quality />
              </Guard>
            }
          />
          <Route
            path="/todo"
            element={
              <Guard permission="todo.use">
                <Todo />
              </Guard>
            }
          />
          <Route
            path="/learning"
            element={
              <Guard permission="elearning.learn">
                <Learning />
              </Guard>
            }
          />
          <Route
            path="/learning/manage"
            element={
              <Guard permission="elearning.manage">
                <LearningManage />
              </Guard>
            }
          />
          <Route
            path="/learning/manage/:id"
            element={
              <Guard permission="elearning.manage">
                <LearningEditor />
              </Guard>
            }
          />
          <Route
            path="/learning/:course"
            element={
              <Guard permission="elearning.learn">
                <LearningCourse />
              </Guard>
            }
          />
          <Route
            path="/learning/:course/:lesson"
            element={
              <Guard permission="elearning.learn">
                <LearningCourse />
              </Guard>
            }
          />
          <Route
            path="/iot"
            element={
              <Guard permission="iot.view">
                <Iot />
              </Guard>
            }
          />
          <Route
            path="/iot/:id"
            element={
              <Guard permission="iot.view">
                <IotDevice />
              </Guard>
            }
          />
          <Route
            path="/payments"
            element={
              <Guard permission="payments.view">
                <Payments />
              </Guard>
            }
          />
          <Route
            path="/shipping"
            element={
              <Guard permission="shipping.view">
                <Shipping />
              </Guard>
            }
          />
          <Route
            path="/payments/:id"
            element={
              <Guard permission="payments.view">
                <PaymentDetail />
              </Guard>
            }
          />
          <Route
            path="/esg"
            element={
              <Guard permission="esg.view">
                <Esg />
              </Guard>
            }
          />
          <Route
            path="/consolidation"
            element={
              <Guard permission="consolidation.view">
                <Consolidation />
              </Guard>
            }
          />
          <Route
            path="/consolidation/mapping/:entity"
            element={
              <Guard permission="consolidation.view">
                <ConsolidationMapping />
              </Guard>
            }
          />
          <Route
            path="/consolidation/:id"
            element={
              <Guard permission="consolidation.view">
                <ConsolidationPeriod />
              </Guard>
            }
          />
          <Route
            path="/billing"
            element={
              <Guard permission="billing.manage">
                <PlanBilling />
              </Guard>
            }
          />
          <Route
            path="/privacy"
            element={
              <Guard permission="privacy.manage">
                <Privacy />
              </Guard>
            }
          />
          <Route
            path="/cleaning"
            element={
              <Guard permission="cleaning.manage">
                <Cleaning />
              </Guard>
            }
          />
          <Route
            path="/frontdesk"
            element={
              <Guard permission="frontdesk.plan">
                <Frontdesk />
              </Guard>
            }
          />
          <Route
            path="/frontdesk/kiosk/:station"
            element={
              <Guard permission="frontdesk.kiosk">
                <FrontdeskKiosk />
              </Guard>
            }
          />
          <Route
            path="/lunch"
            element={
              <Guard permission="lunch.order">
                <Lunch />
              </Guard>
            }
          />
          <Route
            path="/fleet"
            element={
              <Guard permission="fleet.drive">
                <Fleet />
              </Guard>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <Guard permission="recurring.view">
                <Subscriptions />
              </Guard>
            }
          />
          <Route
            path="/marketing"
            element={
              <Guard permission="marketing.view">
                <Marketing />
              </Guard>
            }
          />
          <Route
            path="/outreach"
            element={
              <Guard permission="outreach.view">
                <Outreach />
              </Guard>
            }
          />
          <Route
            path="/sms"
            element={
              <Guard permission="sms.view">
                <Sms />
              </Guard>
            }
          />
          <Route
            path="/social"
            element={
              <Guard permission="social.view">
                <Social />
              </Guard>
            }
          />
          <Route
            path="/calls"
            element={
              <Guard permission="voip.view">
                <Calls />
              </Guard>
            }
          />
          <Route
            path="/helpdesk/*"
            element={
              <Guard permission="helpdesk.view">
                <Helpdesk />
              </Guard>
            }
          />
          <Route
            path="/field"
            element={
              <Guard permission="field.view">
                <FieldService />
              </Guard>
            }
          />
          <Route
            path="/people"
            element={
              <Guard permission="hr.self">
                <People />
              </Guard>
            }
          />
          <Route
            path="/documents"
            element={
              <Guard permission="doc.use">
                <Documents />
              </Guard>
            }
          />
          <Route
            path="/tasks"
            element={
              <Guard permission="project.view">
                <TasksHome />
              </Guard>
            }
          />
          <Route
            path="/tasks/p/:id"
            element={
              <Guard permission="project.view">
                <TaskProject />
              </Guard>
            }
          />
          <Route
            path="/manage/business"
            element={
              <Guard permission="company.manage">
                <Business />
              </Guard>
            }
          />
          <Route
            path="/manage/support"
            element={
              <Guard permission="support.manage">
                <Support />
              </Guard>
            }
          />
          <Route
            path="/manage/locations"
            element={
              <Guard permission="company.manage">
                <Locations />
              </Guard>
            }
          />
          <Route
            path="/manage/taxes"
            element={
              <Guard permission="company.manage">
                <TaxRates />
              </Guard>
            }
          />
          <Route
            path="/manage/currencies"
            element={
              <Guard permission="company.rates">
                <Currencies />
              </Guard>
            }
          />
          <Route
            path="/manage/fields"
            element={
              <Guard permission="studio.manage">
                <Fields />
              </Guard>
            }
          />
          <Route
            path="/manage/automations"
            element={
              <Guard permission="studio.manage">
                <Automations />
              </Guard>
            }
          />
          <Route
            path="/manage/approvals"
            element={
              <Guard permission="studio.manage">
                <ApprovalRules />
              </Guard>
            }
          />
          <Route
            path="/manage/documents"
            element={
              <Guard permission="studio.manage">
                <DocTemplates />
              </Guard>
            }
          />
          <Route
            path="/manage/documents/:id"
            element={
              <Guard permission="studio.manage">
                <DocTemplateEditor />
              </Guard>
            }
          />
          <Route
            path="/approvals"
            element={
              <Guard permission="studio.use">
                <Approvals />
              </Guard>
            }
          />
          <Route
            path="/dashboards"
            element={
              <Guard permission="studio.use">
                <Dashboards />
              </Guard>
            }
          />
          <Route
            path="/dashboards/:id"
            element={
              <Guard permission="studio.use">
                <DashboardPage />
              </Guard>
            }
          />
          <Route
            path="/my-reports"
            element={
              <Guard permission="studio.use">
                <MyReports />
              </Guard>
            }
          />
          <Route
            path="/my-reports/new"
            element={
              <Guard permission="studio.use">
                <ReportBuilder />
              </Guard>
            }
          />
          <Route
            path="/my-reports/:id"
            element={
              <Guard permission="studio.use">
                <MyReport />
              </Guard>
            }
          />
          <Route
            path="/my-reports/:id/edit"
            element={
              <Guard permission="studio.use">
                <ReportBuilder />
              </Guard>
            }
          />
          <Route
            path="/manage/import"
            element={
              <Guard permission="data.import">
                <DataImport />
              </Guard>
            }
          />
          <Route
            path="/manage/fiscal-journal"
            element={
              <Guard permission="fiscal.journal">
                <FiscalJournal />
              </Guard>
            }
          />
          <Route
            path="/manage/fiscal"
            element={
              <Guard permission="fiscal.manage">
                <Fiscal />
              </Guard>
            }
          />
          <Route path="/manage" element={<ManageHome />} />
          <Route path="/design" element={<Design />} />
          <Route path="/apps" element={<Apps />} />
          <Route path="/home" element={<Dashboard />} />
          <Route
            path="/manage/menu"
            element={
              <Guard permission="products.manage">
                <Menu />
              </Guard>
            }
          />
          <Route
            path="/manage/options"
            element={
              <Guard permission="products.manage">
                <Options />
              </Guard>
            }
          />
          <Route
            path="/manage/tables"
            element={
              <Guard permission="restaurant.configure">
                <Tables />
              </Guard>
            }
          />
          <Route
            path="/manage/setup"
            element={
              <Guard permission="restaurant.configure">
                <Setup />
              </Guard>
            }
          />
          <Route
            path="/manage/printers"
            element={
              <Guard permission="printing.manage">
                <Printers />
              </Guard>
            }
          />
          <Route
            path="/manage/happy-hour"
            element={
              <Guard permission="products.manage">
                <PriceTimes />
              </Guard>
            }
          />
          <Route
            path="/manage/stock"
            element={
              <Guard permission="inventory.view">
                <Stock />
              </Guard>
            }
          />
          <Route
            path="/manage/warehouse"
            element={
              <Guard permission="inventory.view">
                <Warehouse />
              </Guard>
            }
          />
          <Route
            path="/manage/recipes"
            element={
              <Guard permission="inventory.view">
                <Recipes />
              </Guard>
            }
          />
          <Route
            path="/manage/purchasing"
            element={
              <Guard permission="purchase.view">
                <Purchasing />
              </Guard>
            }
          />
          <Route
            path="/manage/online"
            element={
              <Guard permission="online.manage">
                <OnlineSetup />
              </Guard>
            }
          />
          <Route
            path="/team"
            element={
              <Guard permission="team.self">
                <Team />
              </Guard>
            }
          />
          <Route
            path="/manage/team"
            element={
              <Guard permission="team.manage">
                <TeamSetup />
              </Guard>
            }
          />
          <Route
            path="/manage/phones"
            element={
              <Guard permission="mobile.manage">
                <Phones />
              </Guard>
            }
          />
          <Route path="/phone" element={<Phone />} />
          <Route
            path="/manage/loyalty"
            element={
              <Guard permission="loyalty.manage">
                <Loyalty />
              </Guard>
            }
          />
          <Route
            path="/manage/roles"
            element={
              <Guard permission="staff.manage">
                <Roles />
              </Guard>
            }
          />
          <Route
            path="/manage/staff"
            element={
              <Guard permission="staff.manage">
                <Staff />
              </Guard>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </PlacesProvider>
  );
}

/** The lines at the top for the staff; never on the customer display, which faces the customer. */
function StaffBanners() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/display/')) return null;
  return (
    <>
      <SupportBanner />
      <AnnouncementBar />
      <PlanBanner />
      <SecurityBanner />
    </>
  );
}
